import { createTheme as createMuiTheme, TypographyVariantsOptions } from '@mui/material/styles';
import { Layout } from 'components/layout/model/Layout';
import { CONTENT_MAX_WIDTH } from 'config/constants';
import { addOpacity } from 'lib/helpers';

declare module '@mui/material/styles' {
    interface Palette {
        titleTextColour: string;
        hint: string;
    }

    interface PaletteOptions {
        titleTextColour?: string;
        hint?: string;
    }
}

const typography: TypographyVariantsOptions = {
    fontSize: 14,
    htmlFontSize: 16,
    fontFamily:
        'TenantFont, SecondaryFont, "Helvetica Neue", HelveticaNeue, Roboto, "Liberation Sans", "Nimbus Sans N", Helvetica, Arial, sans-serif',
    body1: {
        fontWeight: 'normal',
        whiteSpace: 'pre-wrap'
    },
    h6: {
        fontWeight: 'normal',
        fontSize: 18
    }
};

// TODO: Should paper be same colour or change it to white?
export const createTheme = (merchantId: string, layout?: Layout) => {
    if (layout) {
        const titleTextColour = layout?.titleTextColour ? layout?.titleTextColour : layout.anchorTextColour;
        const bodyTextColour = layout?.bodyTextColour ? layout?.bodyTextColour : layout.anchorTextColour;
        const dividerColour = addOpacity(bodyTextColour, 0.12);

        const tenantFont = {
            fontFamily: 'TenantFont',
            src: `url(${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/customfont)`
        };
        const secondaryFont = {
            fontFamily: 'SecondaryFont',
            src: `url(${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/secondary_custom_font)`
        };
        const shadowColourDark = addOpacity(bodyTextColour, 0.2);
        const shadowColourMedium = addOpacity(bodyTextColour, 0.14);
        const shadowColourLight = addOpacity(bodyTextColour, 0.12);
        return createMuiTheme({
            typography,
            palette: {
                text: {
                    primary: bodyTextColour
                },
                primary: {
                    main: layout.primaryActionBackgroundColour,
                    contrastText: layout.primaryActionTextColour
                },
                secondary: {
                    main: bodyTextColour
                },
                background: {
                    default: layout.anchorBackgroundColour,
                    paper: layout.anchorBackgroundColour
                },
                divider: dividerColour,
                hint: layout.tintColour,
                titleTextColour
            },
            shadows: [
                'none',
                `0px 2px 1px -1px ${shadowColourDark}, 0px 1px 1px 0px ${shadowColourMedium}, 0px 1px 3px 0px ${shadowColourLight}`,
                `0px 3px 1px -2px ${shadowColourDark}, 0px 2px 2px 0px ${shadowColourMedium}, 0px 1px 5px 0px ${shadowColourLight}`,
                `0px 3px 3px -2px ${shadowColourDark},0px 3px 4px 0px ${shadowColourMedium},0px 1px 8px 0px ${shadowColourLight}`,
                `0px 2px 4px -1px ${shadowColourDark},0px 4px 5px 0px ${shadowColourMedium},0px 1px 10px 0px ${shadowColourLight}`,
                `0px 3px 5px -1px ${shadowColourDark},0px 5px 8px 0px ${shadowColourMedium},0px 1px 14px 0px ${shadowColourLight}`,
                `0px 3px 5px -1px ${shadowColourDark},0px 6px 10px 0px ${shadowColourMedium},0px 1px 18px 0px ${shadowColourLight}`,
                `0px 4px 5px -2px ${shadowColourDark},0px 7px 10px 1px ${shadowColourMedium},0px 2px 16px 1px ${shadowColourLight}`,
                `0px 5px 5px -3px ${shadowColourDark},0px 8px 10px 1px ${shadowColourMedium},0px 3px 14px 2px ${shadowColourLight}`,
                `0px 5px 6px -3px ${shadowColourDark},0px 9px 12px 1px ${shadowColourMedium},0px 3px 16px 2px ${shadowColourLight}`,
                `0px 6px 6px -3px ${shadowColourDark},0px 10px 14px 1px ${shadowColourMedium},0px 4px 18px 3px ${shadowColourLight}`,
                `0px 6px 7px -4px ${shadowColourDark},0px 11px 15px 1px ${shadowColourMedium},0px 4px 20px 3px ${shadowColourLight}`,
                `0px 7px 8px -4px ${shadowColourDark},0px 12px 17px 2px ${shadowColourMedium},0px 5px 22px 4px ${shadowColourLight}`,
                `0px 7px 8px -4px ${shadowColourDark},0px 13px 19px 2px ${shadowColourMedium},0px 5px 24px 4px ${shadowColourLight}`,
                `0px 7px 9px -4px ${shadowColourDark},0px 14px 21px 2px ${shadowColourMedium},0px 5px 26px 4px ${shadowColourLight}`,
                `0px 8px 9px -5px ${shadowColourDark},0px 15px 22px 2px ${shadowColourMedium},0px 6px 28px 5px ${shadowColourLight}`,
                `0px 8px 10px -5px ${shadowColourDark},0px 16px 24px 2px ${shadowColourMedium},0px 6px 30px 5px ${shadowColourLight}`,
                `0px 8px 11px -5px ${shadowColourDark},0px 17px 26px 2px ${shadowColourMedium},0px 6px 32px 5px ${shadowColourLight}`,
                `0px 9px 11px -5px ${shadowColourDark},0px 18px 28px 2px ${shadowColourMedium},0px 7px 34px 6px ${shadowColourLight}`,
                `0px 9px 12px -6px ${shadowColourDark},0px 19px 29px 2px ${shadowColourMedium},0px 7px 36px 6px ${shadowColourLight}`,
                `0px 10px 13px -6px ${shadowColourDark},0px 20px 31px 3px ${shadowColourMedium},0px 8px 38px 7px ${shadowColourLight}`,
                `0px 10px 13px -6px ${shadowColourDark},0px 21px 33px 3px ${shadowColourMedium},0px 8px 40px 7px ${shadowColourLight}`,
                `0px 10px 14px -6px ${shadowColourDark},0px 22px 35px 3px ${shadowColourMedium},0px 8px 42px 7px ${shadowColourLight}`,
                `0px 11px 14px -7px ${shadowColourDark},0px 23px 36px 3px ${shadowColourMedium},0px 9px 44px 8px ${shadowColourLight}`,
                `0px 11px 15px -7px ${shadowColourDark},0px 24px 38px 3px ${shadowColourMedium},0px 9px 46px 8px ${shadowColourLight}`
            ],
            shape: {
                borderRadius: layout.useRoundedCorners ? 8 : 0
            },
            components: {
                MuiCssBaseline: {
                    styleOverrides: {
                        '.rccs': {
                            width: '100%'
                        },
                        '.rccs__card': {
                            width: 'calc(100vw - 16px)',
                            maxWidth: '450px',
                            height: 'calc(100vw / 1.5858 - 16px / 1.5858)',
                            maxHeight: '283px'
                        },
                        '.rccs__card:not(.rccs__card--unknown) .rccs__card__background': {
                            background: layout.primaryActionBackgroundColour
                        },
                        '.rccs__card--unknown > div': {
                            background: layout.primaryActionBackgroundColour
                        },
                        '.rccs__card--front': {
                            color: layout.primaryActionTextColour
                        },
                        '@font-face': [tenantFont, secondaryFont]
                    }
                },
                MuiIconButton: {
                    styleOverrides: {
                        root: {
                            color: addOpacity(bodyTextColour, 0.54)
                        }
                    }
                },
                MuiFormLabel: {
                    styleOverrides: {
                        root: {
                            color: addOpacity(bodyTextColour, 0.54),
                            '&.Mui-focused': {
                                color: bodyTextColour
                            }
                        }
                    }
                },
                MuiButton: {
                    styleOverrides: {
                        contained: {
                            '&.Mui-disabled': {
                                color: addOpacity(bodyTextColour, 0.2),
                                backgroundColor: addOpacity(bodyTextColour, 0.3),
                                lineHeight: '20px'
                            }
                        }
                    }
                },
                MuiCheckbox: {
                    styleOverrides: {
                        root: {
                            color: layout.tintColour
                        }
                    }
                },
                MuiRadio: {
                    styleOverrides: {
                        root: {
                            color: layout.tintColour
                        },
                        colorPrimary: {
                            color: bodyTextColour
                        }
                    }
                },
                MuiInput: {
                    styleOverrides: {
                        underline: {
                            '&:before': {
                                borderBottomColor: bodyTextColour
                            }
                        }
                    }
                },
                MuiInputBase: {
                    styleOverrides: {
                        root: {
                            '&.Mui-disabled': {
                                color: `${addOpacity(bodyTextColour, 0.38)}`
                            }
                        }
                    }
                },
                MuiInputLabel: {
                    styleOverrides: {
                        outlined: {
                            '&.Mui-disabled': {
                                color: `${addOpacity(bodyTextColour, 0.38)}`,
                                '-webkit-text-fill-color': `${addOpacity(bodyTextColour, 0.38)}` // For autofill override
                            }
                        }
                    }
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        notchedOutline: {
                            borderColor: addOpacity(bodyTextColour, 0.23)
                        },
                        root: {
                            '&.Mui-disabled': {
                                color: `${addOpacity(bodyTextColour, 0.38)}`,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: `${addOpacity(bodyTextColour, 0.38)}`
                                }
                            },
                            '&.Mui-disabled .MuiOutlinedInput-input': {
                                color: `${addOpacity(bodyTextColour, 0.38)}`,
                                '-webkit-text-fill-color': `${addOpacity(bodyTextColour, 0.38)}` // For autofill override
                            }
                        }
                    }
                },
                MuiDialog: {
                    styleOverrides: {
                        paperFullScreen: {
                            maxWidth: `${CONTENT_MAX_WIDTH}px`
                        }
                    }
                },
                MuiDialogTitle: {
                    styleOverrides: {
                        root: {
                            color: titleTextColour
                        }
                    }
                }
            }
        });
    }
    return createMuiTheme({
        typography
    });
};
